import { type LiveBreakWidgetCopy, liveBreakStorefrontPath } from "@loot-os/api-contract";
import {
  type LiveBreakPipAccount,
  LiveBreakPipFrame,
} from "@loot-os/design-system/domain/liveBreaks/LiveBreakPipFrame";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";

import { minimizePip, setPipLayout, usePipState, type WatchableStream } from "../../lib/pip.js";
import { useShopper } from "../../lib/shopper.js";
import { twitchPlayerKeepsSignIn } from "../../lib/twitch.js";
import { useTwitchConnection } from "../../lib/twitchConnection.js";
import { TwitchPipPlayer } from "./TwitchPipPlayer.js";

/**
 * The picture-in-picture player (boxed's `PipViewer`): the design system's frame, where the
 * shopper put it this tab, around Twitch's player for the stream. Its header links the stream's
 * live break, with Join, unless that is the page under it (a promoted stream has none); offers Log
 * in signed out of the store, Sign in to Twitch without a working link (the link's popup, whose
 * twitch.tv sign-in signs the player in too), and Connected once linked, with a way to refresh it.
 * A link that went through remounts the player, which reads its sign-in only on load. ✕ closes it
 * and keeps the pin, so the stream's row comes back in the widget.
 */
export function LiveBreakPip({
  stream,
  words,
  activeLiveBreakId,
}: {
  stream: WatchableStream;
  words: LiveBreakWidgetCopy;
  /** The live break whose page is open, if any: the header doesn't link the page it is on. */
  activeLiveBreakId: string | null;
}) {
  const { layout } = usePipState();
  const { user } = useShopper();
  const navigate = useNavigate();
  const [generation, setGeneration] = useState(0);
  const onLinked = useCallback(() => setGeneration((g) => g + 1), []);
  const twitch = useTwitchConnection({ onLinked });
  const playerStaysSignedOut = useMemo(() => !twitchPlayerKeepsSignIn(), []);

  const account: LiveBreakPipAccount | null = !user
    ? { status: "signedOut", onLogIn: () => void navigate({ to: "/login" }) }
    : twitch.status === "linked"
      ? { status: "linked", onRefresh: twitch.connect, playerStaysSignedOut }
      : twitch.status === "notLinked" || twitch.status === "reconnect"
        ? { status: "notLinked", onConnect: twitch.connect, playerStaysSignedOut }
        : null;

  return (
    <LiveBreakPipFrame
      name={stream.displayName}
      avatarSrc={stream.avatarUrl}
      liveBreakHref={
        stream.liveBreakId && stream.liveBreakId !== activeLiveBreakId
          ? liveBreakStorefrontPath(stream.liveBreakId)
          : null
      }
      words={words}
      account={account}
      onClose={minimizePip}
      layout={layout}
      onLayoutChange={setPipLayout}
    >
      <TwitchPipPlayer
        key={generation}
        channel={stream.channel}
        title={words.pip.label(stream.displayName)}
      />
    </LiveBreakPipFrame>
  );
}
